import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgsApi } from '@/api/organizations';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { formatDate } from '@/lib/formatDate';
import { ApiError } from '@/api/client';
import { UserPlus, Trash2, Mail } from 'lucide-react';

export function TeamPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: queryKeys.orgs.members(orgId),
    queryFn: () => orgsApi.getMembers(orgId),
    enabled: !!orgId,
  });

  const { data: invitationsData } = useQuery({
    queryKey: queryKeys.orgs.invitations(orgId),
    queryFn: () => orgsApi.getInvitations(orgId),
    enabled: !!orgId,
  });

  const inviteMutation = useMutation({
    mutationFn: () => orgsApi.invite(orgId, inviteEmail, inviteRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.orgs.invitations(orgId) });
      setShowInvite(false);
      setInviteEmail('');
      setSuccess('Convite enviado!');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Falha ao enviar convite'),
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => orgsApi.removeMember(orgId, memberId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.orgs.members(orgId) }),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      orgsApi.updateMemberRole(orgId, memberId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.orgs.members(orgId) }),
  });

  const cancelInviteMutation = useMutation({
    mutationFn: (invId: string) => orgsApi.cancelInvitation(orgId, invId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.orgs.invitations(orgId) }),
  });

  const members = membersData?.members ?? [];
  const invitations = (invitationsData?.invitations ?? []).filter((i) => i.status === 'pending');
  const isOwnerOrAdmin = user?.orgRole === 'owner' || user?.orgRole === 'admin';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Equipe</h1>
        {isOwnerOrAdmin && (
          <button
            onClick={() => { setShowInvite(true); setError(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            <UserPlus className="h-4 w-4" /> Convidar Membro
          </button>
        )}
      </div>

      {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>}

      {/* Invite dialog */}
      {showInvite && (
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold mb-3">Convidar um membro para a equipe</h3>
          {error && <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
          <div className="flex gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member' | 'viewer')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="admin">Administrador</option>
              <option value="member">Membro</option>
              <option value="viewer">Visualizador</option>
            </select>
            <button
              onClick={() => inviteMutation.mutate()}
              disabled={!inviteEmail || inviteMutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {inviteMutation.isPending ? 'Enviando...' : 'Enviar'}
            </button>
            <button onClick={() => setShowInvite(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Members */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold">Membros ({members.length})</h2>
        </div>
        {membersLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {members.map((m) => (
              <div key={m.membership.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="font-medium text-sm">{m.user.name}</p>
                  <p className="text-xs text-gray-500">{m.user.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  {isOwnerOrAdmin && m.membership.role !== 'owner' ? (
                    <select
                      value={m.membership.role}
                      onChange={(e) => updateRoleMutation.mutate({ memberId: m.membership.id, role: e.target.value })}
                      className="px-2 py-1 text-xs border border-gray-300 rounded-lg"
                    >
                      <option value="admin">Administrador</option>
                      <option value="member">Membro</option>
                      <option value="viewer">Visualizador</option>
                    </select>
                  ) : (
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 capitalize">{m.membership.role}</span>
                  )}
                  {isOwnerOrAdmin && m.user.id !== user?.id && m.membership.role !== 'owner' && (
                    <button
                      onClick={() => { if (confirm('Remover este membro?')) removeMutation.mutate(m.membership.id); }}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold">Convites Pendentes ({invitations.length})</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">{inv.email}</span>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 capitalize">{inv.role}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">Expira em {formatDate(inv.expiresAt)}</span>
                  {isOwnerOrAdmin && (
                    <button
                      onClick={() => cancelInviteMutation.mutate(inv.id)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
